import { useState } from "react";
import { ChevronDown, ChevronRight, Cloud, Factory, Calculator, Fuel, Zap, Wind } from "lucide-react";

const BoilerEmissionAnalytics = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "co2-emission-calculation",
      title: "CO₂ Emission Calculation",
      description: "Calculates CO₂ emissions for each fuel type (HFO, LFO, MGO) based on fuel consumption and emission factors",
      formula: "CO₂ = Fuel Consumption (kg) × CF_CO₂ (kg/kg-fuel)",
      subFormulas: [
        "CO₂_HFO = BoilerConsHFO × CF_CO₂_HFO",
        "CO₂_LFO = BoilerConsLFO × CF_CO₂_LFO", 
        "CO₂_MGO = BoilerConsMGO × CF_CO₂_MGO",
        "Total CO₂ = CO₂_HFO + CO₂_LFO + CO₂_MGO"
      ],
      icon: <Cloud className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "ch4-emission-calculation",
      title: "Methane (CH₄) Emission Calculation",
      description: "Calculates CH₄ emissions for each fuel type using emission factors",
      formula: "CH₄ = Fuel Consumption (kg) × CF_CH₄ (kg/kg-fuel)",
      subFormulas: [
        "CH₄_HFO = BoilerConsHFO × CF_CH₄_HFO",
        "CH₄_LFO = BoilerConsLFO × CF_CH₄_LFO",
        "CH₄_MGO = BoilerConsMGO × CF_CH₄_MGO",
        "Total CH₄ = CH₄_HFO + CH₄_LFO + CH₄_MGO"
      ],
      icon: <Factory className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "n2o-emission-calculation",
      title: "Nitrous Oxide (N₂O) Emission Calculation",
      description: "Calculates N₂O emissions for each fuel type using emission factors",
      formula: "N₂O = Fuel Consumption (kg) × CF_N₂O (kg/kg-fuel)",
      subFormulas: [
        "N₂O_HFO = BoilerConsHFO × CF_N₂O_HFO",
        "N₂O_LFO = BoilerConsLFO × CF_N₂O_LFO",
        "N₂O_MGO = BoilerConsMGO × CF_N₂O_MGO",
        "Total N₂O = N₂O_HFO + N₂O_LFO + N₂O_MGO"
      ],
      icon: <Factory className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "well-to-tank-calculation",
      title: "Well-to-Tank (WtT) Calculation",
      description: "Calculates upstream emissions from fuel production and transportation",
      formula: "WtT = Σ(Fuel Mass × LCV × WtT × RWD) / Total Energy Input",
      subFormulas: [
        "Fuel Mass (g) = Consumption (kg) × 1,000,000",
        "Total Energy = Σ(Fuel Mass × LCV × RWD) + Shore Power Energy (MJ)",
        "WtT_HFO = HFO Mass × LCV_HFO × WtT_HFO",
        "WtT_LFO = LFO Mass × LCV_LFO × WtT_LFO",
        "WtT_MGO = MGO Mass × LCV_MGO × WtT_MGO",
        "RWD = 2.0 for RFNBO fuels, 1.0 otherwise"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "tank-to-wake-calculation",
      title: "Tank-to-Wake (TtW) Calculation",
      description: "Calculates combustion emissions including methane slip",
      formula: "TtW = Σ[Fuel Mass × ((1-CSlip%)×CO₂eq_TtW + (CSlip%×28))] / Total Energy Input",
      subFormulas: [
        "CO₂eq_TtW = (CF_CO₂ × GWP_CO₂) + (CF_CH₄ × GWP_CH₄) + (CF_N₂O × GWP_N₂O)",
        "GWP_CO₂ = 1, GWP_CH₄ = 28, GWP_N₂O = 298",
        "CSlip = Carbon slip percentage for each fuel",
        "Total Energy = Σ(Fuel Mass × LCV × RWD) + Shore Power Energy (MJ)"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "ghg-intensity-calculation",
      title: "GHG Intensity Calculation",
      description: "Calculates total GHG intensity in g CO₂e/MJ including renewable fuel rewards",
      formula: "GHG Intensity = Reward Factor × (WtT + TtW)",
      subFormulas: [
        "Reward Factor = 1.0 (default)",
        "GHG Intensity = 1.0 × (WtT + TtW)",
        "Units: g CO₂e/MJ"
      ],
      icon: <Wind className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "conversion-to-mt",
      title: "Conversion to Metric Tons (MT)",
      description: "Converts emissions from kilograms to metric tons for reporting",
      formula: "Emissions (MT/day) = Emissions (kg) ÷ 1000",
      subFormulas: [
        "CO₂_MT = Total CO₂ (kg) ÷ 1000",
        "CH₄_MT = Total CH₄ (kg) ÷ 1000",
        "N₂O_MT = Total N₂O (kg) ÷ 1000",
        "GHG reported in g CO₂e/MJ (no conversion needed)"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#d5e7f7] rounded-lg">
              <Cloud className="w-6 h-6 text-[#566573]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Boiler Emission Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Greenhouse gas emission analysis and environmental impact assessment
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Primary Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Detailed Calculations:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoilerEmissionAnalytics;